import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiClient } from '../../utils/apiClient';

// Figma MCP asset URLs (valid ~7 days) — 未使用のため削除

// 予約カレンダーの取得範囲（モジュール定数として定義し、Hook依存から外す）
const RES_RANGE_START = new Date(2025, 0, 1);
const RES_RANGE_END = new Date(2026, 11, 31);

const UserReservationPage: React.FC = () => {
  return (
    <div
      className="bg-white flex flex-col gap-[40px] items-center px-[40px] size-full"
      aria-label="予約ページ"
    >
      {/* Header */}
      <header className="bg-[#001973] flex items-center gap-[24px] px-[16px] py-[24px] w-full">
        <div className="flex items-center gap-[4px]">
          <img
            src="/figma_asset/HeaderIcon.svg"
            alt="SASオンラインロゴ"
            className="w-[40px] h-[27px]"
          />
          <p className="text-[18px] text-[#f5f5f5]">SAS オンライン</p>
        </div>
        <div className="ml-auto">
          <button className="bg-[#c8c8c8] px-[15px] py-[10px] rounded-[30px] text-[12px] text-black">
            ログアウト
          </button>
        </div>
      </header>

      {/* Title */}
      <div className="w-full">
        <p className="text-[24px] font-bold">予約</p>
      </div>

      {/* Calendar block (実表示) */}
      <section className="flex flex-col items-center gap-[20px] px-[16px] rounded-[20px] w-full max-w-[440px]">
        {/* 月週日 切替と日付 */}
        <WeekHeader />

        <WeekDaysHeader />

        {/* 時間軸＋スロット */}
        <CalendarGrid />

        {/* 選択結果 */}
        <SelectedIndicator />
      </section>
    </div>
  );
};

export default UserReservationPage;

// 週ヘッダ（前後ボタン＋中央に日付ラベル）
function WeekHeader() {
  const [weekStart, setWeekStart] = useWeekStartState(RES_RANGE_START);
  const headerLabel = useMemo(() => formatYMDWithWeek(weekStart), [weekStart]);
  const canPrev = addDays(weekStart, -7) >= startOfWeek(RES_RANGE_START);
  const canNext = addDays(weekStart, 7) <= startOfWeek(RES_RANGE_END);

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-[6px]">
        <button
          className={`w-[32px] h-[32px] rounded-full border ${canPrev ? 'border-[#ff7a7a] text-[#ff7a7a]' : 'border-[#cccccc] text-[#cccccc]'}`}
          onClick={() => {
            if (canPrev) {
              const ns = addDays(weekStart, -7);
              setWeekStart(ns);
            }
          }}
          disabled={!canPrev}
        >
          〈
        </button>
        <p className="text-[18px]">{headerLabel}</p>
        <button
          className={`w-[32px] h-[32px] rounded-full border ${canNext ? 'border-[#ff7a7a] text-[#ff7a7a]' : 'border-[#cccccc] text-[#cccccc]'}`}
          onClick={() => {
            if (canNext) {
              const ns = addDays(weekStart, 7);
              setWeekStart(ns);
            }
          }}
          disabled={!canNext}
        >
          〉
        </button>
      </div>
    </div>
  );
}

// 曜日ヘッダ（時間列＋7列の曜日）
function WeekDaysHeader() {
  const [weekStart] = useWeekStartState(RES_RANGE_START);
  const daysOfWeek = useMemo(
    () => Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  return (
    <div className="grid grid-cols-8 w-full border border-[#e5e5e5]">
      <div className="px-[8px] py-[6px] border-r border-[#e5e5e5]" />
      {daysOfWeek.map((d) => (
        <div
          key={d.toISOString()}
          className={`px-[8px] py-[6px] text-center text-[12px] ${isWeekend(d) ? 'text-[#ff7a7a]' : 'text-black'} border-r border-[#e5e5e5] last:border-r-0`}
        >
          {formatDayLabel(d)}
        </div>
      ))}
    </div>
  );
}

// 時間枠グリッド（○をクリック可能）
function CalendarGrid() {
  const navigate = useNavigate();
  const [weekStart] = useWeekStartState(RES_RANGE_START);
  const [occupied] = useWeeklyAppointments();
  const days = 7;
  const hours = useMemo(
    () => [
      '09:00',
      '10:00',
      '11:00',
      '12:00',
      '13:00',
      '14:00',
      '15:00',
      '16:00',
      '17:00',
      '18:00',
      '19:00',
    ],
    []
  );
  const [selected, setSelected] = useReservationSelection();

  return (
    <div className="w-full border border-[#e5e5e5]">
      {hours.map((h) => (
        <div key={h} className="grid grid-cols-8 w-full border-t border-[#e5e5e5] first:border-t-0">
          {/* 左端の時間表示 */}
          <div className="px-[8px] py-[10px] text-[12px] text-[#5a5a5a] border-r border-[#e5e5e5]">
            {h}
          </div>
          {/* 7列の○スロット（各列の日付は weekStart + colIdx） */}
          {Array.from({ length: days }).map((_, colIdx) => {
            const slotKey = `${h}-${colIdx}`;
            const isSelected = selected?.key === slotKey;
            const dateLabel = formatYMD(addDays(weekStart, colIdx));
            const isOccupied = occupied.has(`${dateLabel}-${h}`);
            return (
              <button
                key={slotKey}
                className={`flex items-center justify-center h-[32px] border-r border-[#e5e5e5] last:border-r-0 ${isSelected ? 'bg-[#ffecec]' : ''} ${isOccupied ? 'cursor-not-allowed bg-[#f7f7f7]' : ''}`}
                aria-label={`${formatYMDWithWeek(addDays(weekStart, colIdx))} ${h} スロット`}
                onClick={() => {
                  if (isOccupied) return;
                  setSelected({ key: slotKey, hour: h, dayIndex: colIdx, dateLabel });
                  navigate(
                    `/user/reservation/confirm?date=${encodeURIComponent(dateLabel)}&time=${encodeURIComponent(h)}`
                  );
                }}
                disabled={isOccupied}
              >
                {isOccupied ? (
                  <span className="text-[16px] text-[#b0b0b0]">×</span>
                ) : (
                  <span className="inline-block w-[18px] h-[18px] rounded-full border-2 border-[#4a6bff] bg-white" />
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// 選択表示
function SelectedIndicator() {
  const [selected] = useReservationSelection();
  return (
    <p className="text-[24px] text-black text-center min-h-[32px]">
      {selected ? `${selected.dateLabel ?? ''} ${selected.hour}-` : '時間枠を選択してください'}
    </p>
  );
}

// 予約選択の共有（簡易的にモジュールスコープで状態を保持）
type Selection = { key: string; hour: string; dayIndex: number; dateLabel?: string } | null;
let selectionState: Selection = null;
const listeners = new Set<React.Dispatch<React.SetStateAction<Selection>>>();
function useReservationSelection(): [Selection, (s: Selection) => void] {
  const [state, setState] = useState<Selection>(selectionState);
  const setSelected = (s: Selection) => {
    selectionState = s;
    // ES5ターゲットでも動作するようにforEachを直接使用
    (
      listeners as unknown as {
        forEach: (cb: (l: React.Dispatch<React.SetStateAction<Selection>>) => void) => void;
      }
    ).forEach((l) => l(s));
  };
  React.useEffect(() => {
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return [state, setSelected];
}

// 週開始日の共有（モジュールスコープで保持）
let weekStartState: Date | null = null;
const weekStartListeners = new Set<React.Dispatch<React.SetStateAction<Date>>>();
function useWeekStartState(initial: Date): [Date, (d: Date) => void] {
  const init = weekStartState ?? startOfWeek(initial);
  const [state, setState] = useState<Date>(init);
  const setWeekStart = (d: Date) => {
    weekStartState = startOfWeek(d);
    (
      weekStartListeners as unknown as {
        forEach: (cb: (l: React.Dispatch<React.SetStateAction<Date>>) => void) => void;
      }
    ).forEach((l) => {
      if (weekStartState) l(weekStartState);
    });
  };
  React.useEffect(() => {
    weekStartListeners.add(setState);
    return () => {
      weekStartListeners.delete(setState);
    };
  }, []);
  return [state, setWeekStart];
}

// 週の予約取得と占有スロット管理
type Appointment = {
  id: string;
  doctorId: string;
  patientId: string;
  slotStart: string; // ISO UTC
  slotEnd: string; // ISO UTC
};

let weeklyOccupiedKeys: Set<string> = new Set();
const occupiedListeners = new Set<React.Dispatch<React.SetStateAction<Set<string>>>>();
function useWeeklyAppointments(): [Set<string>, () => void] {
  const [occupied, setOccupied] = useState<Set<string>>(weeklyOccupiedKeys);

  const refetch = useCallback(async () => {
    try {
      // ページアクセス時に全期間分を一括取得（2025-01-01〜2026-12-31）
      const start = RES_RANGE_START;
      const end = RES_RANGE_END;
      const qs = new URLSearchParams({
        doctorId: 'doc-0001',
        start: toUtcIso(start),
        end: toUtcIso(end),
      }).toString();
      const res = await apiClient(`/api/v1/appointments?${qs}`);
      const items: Appointment[] = (res as Appointment[]) ?? [];
      const next = new Set<string>();
      items.forEach((ap) => {
        // UTC → ローカルに変換し、グリッドの時間に丸め（開始時刻の時間単位）
        const d = new Date(ap.slotStart);
        const dateLabel = formatYMD(d);
        const hour = `${pad(d.getHours())}:00`;
        next.add(`${dateLabel}-${hour}`);
      });
      weeklyOccupiedKeys = next;
      (
        occupiedListeners as unknown as {
          forEach: (cb: (l: React.Dispatch<React.SetStateAction<Set<string>>>) => void) => void;
        }
      ).forEach((l) => l(next));
    } catch (e) {
      // 失敗時は占有なしとする（ユーザーには別途通知検討）
      weeklyOccupiedKeys = new Set();
      (
        occupiedListeners as unknown as {
          forEach: (cb: (l: React.Dispatch<React.SetStateAction<Set<string>>>) => void) => void;
        }
      ).forEach((l) => l(weeklyOccupiedKeys));
    }
  }, []);

  useEffect(() => {
    // 初回マウント時に現在の週で取得
    refetch();
  }, [refetch]);

  useEffect(() => {
    occupiedListeners.add(setOccupied);
    return () => {
      occupiedListeners.delete(setOccupied);
    };
  }, []);

  return [occupied, refetch];
}

// 日付ユーティリティ
function startOfWeek(d: Date): Date {
  const nd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = nd.getDay(); // 0=日,1=月,...
  // Figma見た目に合わせ、週は火曜始まりではなく一般的な月曜始まりを採用（要件に合わせ可変可能）
  const delta = (day + 6) % 7; // 月曜=0
  nd.setDate(nd.getDate() - delta);
  nd.setHours(0, 0, 0, 0);
  return nd;
}
function addDays(d: Date, n: number): Date {
  const nd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  nd.setDate(nd.getDate() + n);
  return nd;
}
function isWeekend(d: Date): boolean {
  const wd = d.getDay();
  return wd === 0 || wd === 6;
}
function formatDayLabel(d: Date): string {
  const wd = ['日', '月', '火', '水', '木', '金', '土'][d.getDay()];
  return `${wd} ${d.getDate()}`;
}
function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}
function formatYMD(d: Date): string {
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
}
function formatYMDWithWeek(d: Date): string {
  const wd = ['日', '月', '火', '水', '木', '金', '土'][d.getDay()];
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} (${wd})`;
}
function toUtcIso(d: Date): string {
  const dd = new Date(d.getTime());
  // 週境界の厳密さのため 00:00:00Z 固定
  dd.setHours(0, 0, 0, 0);
  return new Date(Date.UTC(dd.getFullYear(), dd.getMonth(), dd.getDate())).toISOString();
}
